/**
 * AdminDashboardPage - Admin Analytics Dashboard
 *
 * 9-tab analytics interface:
 * Visão Geral, Funil, Aquisição, Onboarding, Receita, Eventos
 */

import React from 'react';
import { useSearchParams } from 'react-router-dom';
import AdminOverviewTab from '@/components/admin/AdminOverviewTab';
import AdminHelpTab from '@/components/admin/AdminHelpTab';
import AdminFunnelTab from '@/components/admin/AdminFunnelTab';
import AdminAcquisitionTab from '@/components/admin/AdminAcquisitionTab';
import AdminOnboardingTab from '@/components/admin/AdminOnboardingTab';
import AdminRevenueTab from '@/components/admin/AdminRevenueTab';
import AdminEventsTab from '@/components/admin/AdminEventsTab';
import AdminChannelsTab from '@/components/admin/AdminChannelsTab';
import AdminLeadsExportTab from '@/components/admin/AdminLeadsExportTab';
import { cn } from '@/lib/utils';

type AnalyticsTab = 'overview' | 'help' | 'funnel' | 'acquisition' | 'onboarding' | 'revenue' | 'channels' | 'events' | 'leads';

const TABS: { id: AnalyticsTab; label: string }[] = [
  { id: 'overview', label: 'Visão Geral' },
  { id: 'help', label: 'Suporte' },
  { id: 'funnel', label: 'Funil' },
  { id: 'acquisition', label: 'Aquisição' },
  { id: 'onboarding', label: 'Onboarding' },
  { id: 'revenue', label: 'Receita' },
  { id: 'channels', label: 'Canais & Links' },
  { id: 'events', label: 'Eventos' },
  { id: 'leads', label: 'Exportar Leads' },
];

const isAnalyticsTab = (value: string | null): value is AnalyticsTab => TABS.some((tab) => tab.id === value);

const AdminDashboardPage: React.FC = () => {
  // A aba vive em ?tab= para links diretos (ex.: o alerta do Telegram abre ?tab=help&conversation=<id>).
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const activeTab: AnalyticsTab = isAnalyticsTab(tabParam) ? tabParam : 'overview';

  const selectTab = (id: AnalyticsTab) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('tab', id);
        next.delete('conversation');
        return next;
      },
      { replace: true }
    );

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      <h1 className="text-2xl font-bold text-foreground">Admin Dashboard</h1>

      <div className="flex gap-1 border-b border-border overflow-x-auto">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => selectTab(tab.id)}
            className={cn(
              'px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap shrink-0',
              activeTab === tab.id
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'overview' && <AdminOverviewTab />}
      {activeTab === 'help' && <AdminHelpTab />}
      {activeTab === 'funnel' && <AdminFunnelTab />}
      {activeTab === 'acquisition' && <AdminAcquisitionTab />}
      {activeTab === 'onboarding' && <AdminOnboardingTab />}
      {activeTab === 'revenue' && <AdminRevenueTab />}
      {activeTab === 'channels' && <AdminChannelsTab />}
      {activeTab === 'events' && <AdminEventsTab />}
      {activeTab === 'leads' && <AdminLeadsExportTab />}
    </div>
  );
};

export default AdminDashboardPage;
